/** 对账屏取数与解析（G3，#4604）。
 *
 * 形状以后端为准（主人 2026-10-08 裁决）：`GET /reconciliations` 返回**裸数组**，每行
 * `run_id`、`balanced`、`checked`、`missing_locally[]`、`missing_on_channel[]`、`mismatched[]`、`summary`；
 * `strategy_id` / `trade_date` / `ts_ns` / `channel` 是屏规格里「待加」的四个字段，后端给了就读，没给记 `null`
 * （界面显示「缺」，不猜）。
 *
 * 三类差异分列，**不合并**（operations-console「对账可视」）。
 */

import { apiUrl, expectRecord, optionalNumber, optionalString, query, requireBoolean, requireNumber, requireString } from "../../api/client";
import { ApiError, ContractError } from "../../api/errors";

export interface ReconRow {
  run_id: string;
  /** 该次运行没有对账结果（run.json 里没有 reconciliation 段）。 */
  unreconciled: boolean;
  balanced: boolean | null;
  checked: number | null;
  missing_locally: string[];
  missing_on_channel: string[];
  mismatched: string[];
  summary: string | null;
  strategy_id: string | null;
  trade_date: string | null;
  ts_ns: number | null;
  channel: string | null;
}

const RECON_KEYS = ["balanced", "checked", "missing_locally", "missing_on_channel", "mismatched", "summary"] as const;

function stringList(source: Record<string, unknown>, key: string, url: string): string[] {
  const value = source[key];
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) {
    throw new ContractError(`字段 \`${key}\` 应为字符串数组`, url, source);
  }
  return value as string[];
}

/** 一行对账结果。没有任何对账字段 → 「未对账」；只给了一部分 → 契约违约（不猜）。 */
export function parseReconRow(value: unknown, url: string): ReconRow {
  const row = expectRecord(value, url, "对账行");
  const runId = requireString(row, "run_id", url);
  const meta = {
    strategy_id: optionalString(row, "strategy_id"),
    trade_date: optionalString(row, "trade_date"),
    ts_ns: optionalNumber(row, "ts_ns"),
    channel: optionalString(row, "channel"),
  };
  if (RECON_KEYS.every((key) => !(key in row))) {
    return { run_id: runId, unreconciled: true, balanced: null, checked: null, missing_locally: [], missing_on_channel: [], mismatched: [], summary: null, ...meta };
  }
  return {
    run_id: runId,
    unreconciled: false,
    balanced: requireBoolean(row, "balanced", url),
    checked: requireNumber(row, "checked", url),
    missing_locally: stringList(row, "missing_locally", url),
    missing_on_channel: stringList(row, "missing_on_channel", url),
    mismatched: stringList(row, "mismatched", url),
    summary: requireString(row, "summary", url),
    ...meta,
  };
}

/** `GET /reconciliations`：后端返回裸数组。对象包裹不是后端形状，按契约违约处理。 */
export function parseReconList(body: unknown): ReconRow[] {
  const url = apiUrl("/reconciliations");
  if (!Array.isArray(body)) throw new ContractError("对账历史应为数组（后端返回裸数组）", url, body);
  return body.map((item) => parseReconRow(item, url));
}

export const readReconList = (): Promise<ReconRow[]> => query<unknown>("/reconciliations").then(parseReconList);

/** 单次明细；404 → `null`（界面显示「未对账」，按 run_id 索引由缺陷单 #4748 修）。 */
export async function readReconDetail(runId: string): Promise<ReconRow | null> {
  const path = `/reconciliations/${encodeURIComponent(runId)}`;
  try {
    const body = await query<unknown>(path);
    return parseReconRow(body, apiUrl(path));
  } catch (error) {
    if (error instanceof ApiError && error.kind === "http" && error.status === 404) return null;
    throw error;
  }
}

/** 结论文案：区分「未对账」「平（无成交）」「核对 N 笔全平」「不平」。 */
export function verdict(row: Pick<ReconRow, "unreconciled" | "balanced" | "checked">): { text: string; tone: "ok" | "bad" | "neutral" } {
  if (row.unreconciled || row.balanced === null) return { text: "未对账", tone: "neutral" };
  if (!row.balanced) return { text: "不平", tone: "bad" };
  if (row.checked === 0) return { text: "平（当日无成交，核对 0 笔）", tone: "ok" };
  return { text: `平（核对 ${row.checked} 笔）`, tone: "ok" };
}

/** 页头汇总：只数后端给的 `balanced`，前端不比对成交明细。 */
export function summarize(rows: ReconRow[]): { total: number; balanced: number; broken: number; unreconciled: number; allBalanced: boolean } {
  const balanced = rows.filter((row) => row.balanced === true).length;
  const broken = rows.filter((row) => row.balanced === false).length;
  const unreconciled = rows.filter((row) => row.unreconciled).length;
  return { total: rows.length, balanced, broken, unreconciled, allBalanced: rows.length > 0 && balanced === rows.length };
}

/** 排序：不平的行置顶（导致停机），其余保持后端顺序。 */
export function brokenFirst(rows: ReconRow[]): ReconRow[] {
  return [...rows.filter((row) => row.balanced === false), ...rows.filter((row) => row.balanced !== false)];
}
